import { useState, useEffect, useRef, useCallback } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import ActivityFilters from '@/components/ActivityFilters'
import EmailActivityCard from '@/components/EmailActivityCard'
import EmptyState from '@/components/EmptyState'
import { Card, CardContent } from '@/components/ui/card'

interface Email {
  id: number
  fromName: string | null
  fromEmail: string | null
  subject: string | null
  receivedAt: string
  gmailMessageId: string
  processingStatus: string
  stats: {
    workflowsPassed: number
    workflowsFailed: number
    totalActions: number
    totalWorkflows: number
  }
  processingLogs: Array<{
    id: number
    workflowId: number | null
    status: string
    startedAt: string | null
    completedAt: string | null
  }>
}

interface IndexProps {
  account: {
    id: number
    name: string
    gmailSessionId?: number
  }
  emails: Email[]
  pagination: {
    page: number
    perPage: number
    totalCount: number
    totalPages: number
  }
  filters: {
    query: string
    timeRange: string
  }
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
    timezone?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

export default function Index({
  account,
  emails: initialEmails,
  pagination: initialPagination,
  filters,
  currentUser,
  gmailConnection,
}: IndexProps) {
  const [emails, setEmails] = useState<Email[]>(initialEmails)
  const [pagination, setPagination] = useState(initialPagination)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(initialPagination.page < initialPagination.totalPages)
  const [isPolling, setIsPolling] = useState(false)
  const observerTarget = useRef<HTMLDivElement>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Sync emails when props change (from filters/search)
  useEffect(() => {
    setEmails(initialEmails)
    setPagination(initialPagination)
    setHasMore(initialPagination.page < initialPagination.totalPages)
  }, [initialEmails, initialPagination])

  const loadMoreEmails = useCallback(() => {
    if (isLoadingMore || !hasMore) return

    setIsLoadingMore(true)
    const nextPage = pagination.page + 1

    router.get(
      `/accounts/${account.id}/activity`,
      {
        page: nextPage,
        query: filters.query || undefined,
        time_range: filters.timeRange,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ['emails', 'pagination'],
        onSuccess: (page) => {
          const newEmails = page.props.emails as Email[]
          const newPagination = page.props.pagination as typeof pagination

          setEmails((prev) => [...prev, ...newEmails])
          setPagination(newPagination)
          setHasMore(newPagination.page < newPagination.totalPages)
          setIsLoadingMore(false)
        },
        onError: () => {
          setIsLoadingMore(false)
        },
      }
    )
  }, [account.id, filters.query, filters.timeRange, pagination.page, hasMore, isLoadingMore])

  // Polling for processing status updates
  useEffect(() => {
    // Check if any emails are processing
    const hasProcessingEmails = emails.some(
      (email) => email.processingStatus === 'processing'
    )

    if (hasProcessingEmails && !isPolling) {
      setIsPolling(true)
      pollingIntervalRef.current = setInterval(() => {
        router.reload({
          only: ['emails', 'pagination'],
          preserveState: false,
          preserveScroll: true,
          onSuccess: (page) => {
            const newEmails = page.props.emails as Email[]
            setEmails(newEmails)
            setPagination(page.props.pagination as typeof pagination)

            // Stop polling if no processing emails remain
            const stillProcessing = newEmails.some(
              (email) => email.processingStatus === 'processing'
            )
            if (!stillProcessing) {
              setIsPolling(false)
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
              }
            }
          },
        })
      }, 4000) // Poll every 4 seconds
    } else if (!hasProcessingEmails && isPolling) {
      setIsPolling(false)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [emails, isPolling])

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreEmails()
        }
      },
      { threshold: 0.1 }
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [hasMore, isLoadingMore, loadMoreEmails])

  const handleEmailClick = (emailId: number) => {
    router.get(`/accounts/${account.id}/activity/${emailId}`)
  }

  const totalEmails = pagination.totalCount
  const hasResults = emails.length > 0
  const hasSearchQuery = filters.query.length > 0

  return (
    <>
      <Head title="Activity" />
      <DashboardLayout
        currentUser={currentUser}
        gmailConnection={gmailConnection}
        account={account}
        activeTab="activity"
      >
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-950 dark:text-white mb-2">
            Activity Feed
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {totalEmails === 0
              ? 'No emails'
              : `${totalEmails} ${totalEmails === 1 ? 'email' : 'emails'} Real-time updates`}
          </p>
        </div>

        {/* Filters */}
        {hasResults && (
          <ActivityFilters
            query={filters.query}
            timeRange={filters.timeRange}
            accountId={account.id}
          />
        )}

        {/* Email List */}
        {hasResults ? (
          <>
            <Card className="rounded-lg overflow-hidden">
              <CardContent className="p-0">
                {emails.map((email, index) => (
                  <EmailActivityCard
                    key={email.id}
                    email={email}
                    onClick={() => handleEmailClick(email.id)}
                    gmailSessionId={account.gmailSessionId}
                    isFirst={index === 0}
                    isLast={index === emails.length - 1}
                    timezone={currentUser?.timezone || 'America/New_York'}
                  />
                ))}
              </CardContent>
            </Card>

            {/* Infinite Scroll Trigger */}
            <div ref={observerTarget} className="h-10 flex items-center justify-center">
              {isLoadingMore && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Loading more emails...
                </p>
              )}
            </div>

            {/* End of List */}
            {!hasMore && emails.length > 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You've reached the end of the activity feed
                </p>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title="No emails found"
            description={
              hasSearchQuery
                ? 'Try adjusting your search query or time filter'
                : 'Emails will appear here as they are processed through your workflows'
            }
          />
        )}
      </div>
    </DashboardLayout>
    </>
  )
}
